'use client';

import { useEffect } from 'react';

const revealSelector = '[data-section-reveal], [data-reveal], [data-divider-reveal]';

export default function ScrollReveals() {
    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const observed = new WeakSet<Element>();
        const revealImmediately = (element: Element) => {
            if (element.getBoundingClientRect().top <= window.innerHeight * 0.92) {
                element.classList.add('is-visible');
                return true;
            }
            return false;
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                });
            },
            { threshold: 0.08, rootMargin: '0px 0px -8%' },
        );

        const observeElements = (root: ParentNode) => {
            const elements = [
                ...(root instanceof Element && root.matches(revealSelector) ? [root] : []),
                ...Array.from(root.querySelectorAll(revealSelector)),
            ];

            elements.forEach((element) => {
                if (observed.has(element)) return;
                observed.add(element);
                if (!revealImmediately(element)) observer.observe(element);
            });
        };

        observeElements(document);
        document.documentElement.classList.add('section-reveals-enabled');

        const mutationObserver = new MutationObserver((entries) => {
            entries.forEach((entry) => {
                entry.addedNodes.forEach((node) => {
                    if (node instanceof Element) observeElements(node);
                });
            });
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });

        return () => {
            mutationObserver.disconnect();
            observer.disconnect();
            document.documentElement.classList.remove('section-reveals-enabled');
        };
    }, []);

    return null;
}
